import React, { useState, useEffect } from "react";
import "./ComplaintHandlingpage.css";
import ComplaintModal from "../orders/complaint/ComplaintModal.jsx";
import { useComplaints } from "../../hooks/useComplaints";
import { useToast } from "../../mesageToast/ToastContainer";
import SupportTickets from "./SupportTickets/SupportTickets";
import { useLocation, useNavigate } from "react-router-dom";

// Utility function to get current user ID from token
const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;

    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload._id || payload.id || payload.userId;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

const ComplaintHandling = () => {
  const [activeTab, setActiveTab] = useState("raised");
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemPopup, setItemPopup] = useState({ show: false, items: [] });
  const addToast = useToast();
  const {
    complaints,
    loading,
    error,
    getComplaints,
    clearComplaintError,
    getFilteredComplaints,
  } = useComplaints();
  const [currentUserId, setCurrentUserId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isSupportMode = location.pathname.includes("/MOD/complaints/support");

  useEffect(() => {
    const userId = getCurrentUserId();
    setCurrentUserId(userId);

    // Default tab is "raised"
    getComplaints({ status: "raised" });
  }, [getComplaints]);

  useEffect(() => {
    if (error) {
      addToast("Error", error, "error");
      clearComplaintError();
    }
  }, [error, addToast, clearComplaintError]);

  const handleTabChange = (tab) => {
    if (tab === "support") {
      navigate("/MOD/complaints/support");
      return;
    }

    if (location.pathname.includes("/MOD/complaints/support")) {
      navigate("/MOD/complaints");
    }

    setActiveTab(tab);

    // Fetch complaints based on tab with appropriate filters
    if (tab === "resolved" && currentUserId) {
      getComplaints({ status: "resolved", userId: currentUserId });
    } else if (tab === "raised") {
      getComplaints({ status: "raised" });
    } else {
      getComplaints();
    }
  };

  const filteredComplaints = () => {
    // Since we're now fetching filtered data from backend, just return all complaints
    return complaints;
  };

  const handleViewComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedComplaint(null);
  };

  const handleUpdateComplaints = () => {
    // Refresh complaints based on current tab
    if (activeTab === "raised") {
      getComplaints({ status: "raised" });
    } else if (activeTab === "resolved" && currentUserId) {
      getComplaints({ status: "resolved", userId: currentUserId });
    } else {
      getComplaints();
    }
    handleCloseModal();
    addToast("Success", "Complaint resolved successfully", "success");
  };

  const calculateTimeTaken = (createdAt, resolvedAt) => {
    if (!resolvedAt) return "N/A";
    const created = new Date(createdAt);
    const resolved = new Date(resolvedAt);
    const diffMs = resolved - created;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const diffSecs = Math.floor((diffMs % (1000 * 60)) / 1000);
    return `${diffHours}h ${diffMins}m ${diffSecs}s`;
  };

  const getResolvedTime = (complaint) => {
    const resolvedHistory = complaint.history?.find(
      (h) => h.status === "resolved",
    );
    return resolvedHistory ? resolvedHistory.timestamp : null;
  };

  const renderTableHeaders = () => {
    const commonHeaders = [
      "Case No",
      "Complaint ID",
      "Restaurant Name",
      "Location",
      "Created At",
      "Items & Price",
      "Total Item Price",
      "Order Value",
      "Order Status",
      "Resolution Status",
    ];

    if (activeTab === "raised") {
      return [...commonHeaders, "Action"];
    } else if (activeTab === "resolved") {
      return [...commonHeaders, "Support Team Message", "Time Taken", "View"];
    }
    return commonHeaders;
  };

  const renderTableRow = (complaint, index) => {
    const resolvedTime = getResolvedTime(complaint);
    const timeTaken = calculateTimeTaken(complaint.createdAt, resolvedTime);
    const supportMessage =
      complaint.message?.find((m) => m.userType === "support team")?.content ||
      "N/A";

    return (
      <tr key={complaint._id || index}>
        <td style={{ whiteSpace: "nowrap" }}>{complaint.operationId}</td>
        <td style={{ whiteSpace: "nowrap" }}>{complaint._id}</td>
        <td>{complaint.restaurant || "N/A"}</td>
        <td>{complaint.location || "N/A"}</td>
        <td>{new Date(complaint.createdAt).toLocaleDateString()}</td>
        <td>
          {complaint.selectitem?.length > 0 ? (
            <button
              className="view-btn"
              onClick={() =>
                setItemPopup({ show: true, items: complaint.selectitem })
              }
            >
              Show
            </button>
          ) : (
            "N/A"
          )}
        </td>
        <td>
          ₹
          {complaint.selectitem?.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0,
          ) || 0}
        </td>
        <td>₹{complaint.price || 0}</td>
        <td>{complaint.orderStatus || "N/A"}</td>
        <td>
          <span className="status-badge">{complaint.status}</span>
        </td>
        {activeTab === "raised" && (
          <>
            <td>
              <button
                className="action-btn"
                onClick={() => handleViewComplaint(complaint)}
              >
                Action
              </button>
            </td>
          </>
        )}
        {activeTab === "resolved" && (
          <>
            <td>{supportMessage}</td>
            <td>{timeTaken}</td>
            <td>
              <button
                className="view-btn"
                onClick={() => handleViewComplaint(complaint)}
              >
                View
              </button>
            </td>
          </>
        )}
      </tr>
    );
  };

  return (
    <div className="complaint-handlingpage">
      <div className="complaint-tabs">
        <button
          className={`tab-btn ${activeTab === "raised" && !isSupportMode ? "active" : ""}`}
          onClick={() => handleTabChange("raised")}
        >
          Raised
        </button>
        <button
          className={`tab-btn ${activeTab === "inprogress" && !isSupportMode ? "active" : ""}`}
          onClick={() => handleTabChange("inprogress")}
        >
          In Progress
        </button>
        <button
          className={`tab-btn ${activeTab === "resolved" && !isSupportMode ? "active" : ""}`}
          onClick={() => handleTabChange("resolved")}
        >
          Resolved
        </button>
        <button
          className={`tab-btn ${isSupportMode ? "active" : ""}`}
          onClick={() => handleTabChange("support")}
        >
          Support Tickets
        </button>
      </div>

      <div className="complaint-content">
        {isSupportMode ? (
          <SupportTickets />
        ) : loading ? (
          <div className="loading">Loading complaints...</div>
        ) : activeTab === "inprogress" ? (
          <div className="no-data">No in progress complaints available</div>
        ) : (
          <div className="table-container">
            <table className="complaint-table">
              <thead>
                <tr>
                  {renderTableHeaders().map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredComplaints().length > 0 ? (
                  filteredComplaints().map((complaint, index) =>
                    renderTableRow(complaint, index),
                  )
                ) : (
                  <tr>
                    <td
                      colSpan={renderTableHeaders().length}
                      className="no-data"
                    >
                      No {activeTab} complaints found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <ComplaintModal
          ticket={selectedComplaint}
          onClose={handleCloseModal}
          onUpdate={handleUpdateComplaints}
        />
      )}

      {itemPopup.show && (
        <div
          className="modal-overlay"
          onClick={() => setItemPopup({ show: false, items: [] })}
        >
          <div className="complaint-modal" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Complaint Item Details</h3>
              <button
                className="close-btn"
                onClick={() => setItemPopup({ show: false, items: [] })}
              >
                ×
              </button>
            </div>
            <div className="order-items">
              {itemPopup.items.map((item, idx) => (
                <div
                  key={idx}
                  className="item-detail"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span className="item-name">{item.name}</span>
                  <span className="item-qty">Qty: {item.quantity}</span>
                  <span className="item-price">₹{item.price || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintHandling;
